import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import ptBR from 'antd/locale/pt_BR';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { pepollTheme } from './config/theme.config';
import { AuthenticatedLayout } from './components/layout/AuthenticatedLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ConfirmAccountPage } from './pages/ConfirmAccountPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { PublicHomePage } from './pages/PublicHomePage';
import LegalPage from './pages/legal/LegalPage';
import { PublicHomeLayout } from './components/layout/PublicHomeLayout';
import { ProfilePage } from './pages/ProfilePage';
import { AdminDashboard } from './pages/admin/Dashboard';
import { ContractorsPage } from './pages/admin/Contractors';
import { ContractorDetail } from './pages/admin/ContractorDetail';
import { SurveysPage as AdminSurveysPage } from './pages/admin/Surveys';
import { AdminTagsPage } from './pages/admin/Tags';
import { SurveyDetail } from './pages/admin/SurveyDetail';
import { LegalDocumentsPage } from './pages/admin/legal/LegalDocumentsPage';
import { AdminUsers } from './pages/admin/Users';
import { BulkEmail } from './pages/admin/BulkEmail';
import { BulkEmailCampaignDetail } from './pages/admin/BulkEmailCampaignDetail';
import EngagementCycleManagementPage from './pages/admin/EngagementCycleManagementPage';
import { LotteryRegulationPage } from './pages/admin/LotteryRegulationPage';
import { EngagementCycleReportPage } from './pages/admin/EngagementCycleReportPage';
import ContractorDashboard from './pages/contractor/ContractorDashboard';
import ContractorSurveyDetail from './pages/contractor/ContractorSurveyDetail';
import ContractorReports from './pages/contractor/ContractorReports';
import { ContractorSurveys } from './pages/contractor/ContractorSurveys';
import { ContractorSurveyEdit } from './pages/contractor/ContractorSurveyEdit';
import SurveyAnalysisPage from './pages/contractor/SurveyAnalysisPage';
import SurveyQuestionnaireReport from './pages/contractor/SurveyQuestionnaireReport';
import ParticipantDashboard from './pages/participant/ParticipantDashboard';
import ParticipantEngagementPage from './pages/participant/ParticipantEngagementPage';
import TakeSurvey from './pages/participant/TakeSurvey';
import ParticipantResponseDetail from './pages/participant/ParticipantResponseDetail';
import ParticipantSurveyAnalytics from './pages/participant/ParticipantSurveyAnalytics';
import PublicAuditTrailPage from './pages/PublicAuditTrailPage';
import { InviteAcceptPage } from './pages/public/InviteAcceptPage';
import { SurveyLandingPage } from './pages/public/SurveyLandingPage';
import SurveyPhoneOtpPage from './pages/public/SurveyPhoneOtpPage';
import GuestPhoneChangePage from './pages/public/GuestPhoneChangePage';
import GuestAccountLinkPage from './pages/public/GuestAccountLinkPage';
import { LinkNotFoundPage } from './pages/public/LinkNotFoundPage';
import { SurveyUnavailablePage } from './pages/public/SurveyUnavailablePage';
import { FaqPage } from './pages/FaqPage';
import { AdminFaqPage } from './pages/admin/AdminFaqPage';
import AdminSurveyAnalysisPage from './pages/admin/AdminSurveyAnalysisPage';
import AdminSurveyPreviewAnalysisPage from './pages/admin/AdminSurveyPreviewAnalysisPage';
import AdminAiModelsPage from './pages/admin/AdminAiModelsPage';
import { CookieBanner } from './components/consent/CookieBanner';
import { useGuestSession } from './services/useGuestSession';
import 'dayjs/locale/pt-br';
import dayjs from 'dayjs';

// Configure dayjs locale for Ant Design DatePicker
dayjs.locale('pt-br');

// Rota pública para convidados responderem pesquisas (sem ProtectedRoute)
function GuestTakeSurveyRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const guestSession = useGuestSession();

  if (isLoading) return <LoadingSpinner />;

  // Usuário autenticado: usa o fluxo normal
  if (isAuthenticated) {
    return (
      <AuthenticatedLayout>
        <TakeSurvey />
      </AuthenticatedLayout>
    );
  }

  // Convidado com sessão ativa: layout minimal sem sidebar
  if (guestSession.isActive) {
    return <TakeSurvey />;
  }

  // Nenhuma sessão: redireciona para login
  return <Navigate to="/login" replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'ADMINISTRATOR') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function ContractorRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'CONTRACTOR' && user?.role !== 'ADMINISTRATOR') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { isLoading } = useAuth();

  const rootElement = isLoading ? <LoadingSpinner /> : <PublicHomePage />;

  return (
    <Routes>
      <Route path="/" element={rootElement} />
      <Route path="/login" element={<LoginForm />} />
      <Route path="/register" element={<RegisterForm />} />
      <Route path="/signup" element={<RegisterForm />} />
      <Route path="/confirm-account" element={<ConfirmAccountPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/public" element={<Navigate to="/" replace />} />
      <Route path="/legal" element={<Navigate to="/legal/termos-de-uso" replace />} />
      <Route path="/faq" element={<PublicHomeLayout><FaqPage /></PublicHomeLayout>} />
      <Route path="/legal/termos-de-uso" element={<PublicHomeLayout><LegalPage /></PublicHomeLayout>} />
      <Route path="/legal/termos-de-servico" element={<Navigate to="/legal/termos-de-uso" replace />} />
      <Route path="/legal/politica-de-privacidade" element={<PublicHomeLayout><LegalPage /></PublicHomeLayout>} />
      <Route path="/legal/politica-de-cookies" element={<PublicHomeLayout><LegalPage /></PublicHomeLayout>} />
      <Route path="/public/surveys" element={<Navigate to="/" replace />} />
      <Route path="/perfil" element={<Navigate to="/profile" replace />} />
      <Route path="/public/surveys/:surveyHash" element={<SurveyLandingPage />} />
      <Route path="/public/surveys/:surveyHash/participar-telefone" element={<SurveyPhoneOtpPage />} />
      <Route path="/guest/phone-change" element={<GuestPhoneChangePage />} />
      <Route path="/guest/account-link" element={<GuestAccountLinkPage />} />
      <Route path="/surveys/:surveyId/guest-take" element={<GuestTakeSurveyRoute />} />
      <Route path="/public/surveys/:surveyHash/audit-trail" element={<PublicAuditTrailPage />} />
      <Route path="/invite/:hash" element={<InviteAcceptPage />} />
      <Route path="/link-not-found" element={<LinkNotFoundPage />} />
      <Route path="/survey-unavailable" element={<SurveyUnavailablePage />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ProfilePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ProfilePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/engagement"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ParticipantEngagementPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ParticipantDashboard />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:surveyId/take"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <TakeSurvey />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:surveyId/responses"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ParticipantResponseDetail />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys/:surveyId/analytics"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ParticipantSurveyAnalytics />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <AuthenticatedLayout>
              <Routes>
                <Route path="/" element={<AdminDashboard />} />
                <Route path="/contractors/new" element={<ContractorDetail />} />
                <Route path="/contractors" element={<ContractorsPage />} />
                <Route path="/contractors/:id" element={<ContractorDetail />} />
                <Route path="/surveys/new" element={<SurveyDetail />} />
                <Route path="/surveys" element={<AdminSurveysPage />} />
                <Route path="/surveys/:id" element={<SurveyDetail />} />
                <Route path="/surveys/:surveyId/questionnaire-report" element={<SurveyQuestionnaireReport />} />
                <Route path="/surveys/:surveyId/analysis" element={<AdminSurveyAnalysisPage />} />
                <Route path="/surveys/:surveyId/analysis/preview" element={<AdminSurveyPreviewAnalysisPage />} />
                <Route path="/tags" element={<AdminTagsPage />} />
                <Route path="/users" element={<AdminUsers />} />
                <Route path="/bulk-email" element={<BulkEmail />} />
                <Route path="/bulk-email/campaigns/:id" element={<BulkEmailCampaignDetail />} />
                <Route path="/legal" element={<LegalDocumentsPage />} />
                <Route path="/engagement/cycles" element={<EngagementCycleManagementPage />} />
                <Route path="/engagement/cycles/:cycleKey/report" element={<EngagementCycleReportPage />} />
                <Route path="/engagement/regulation" element={<LotteryRegulationPage />} />
                <Route path="/faq" element={<AdminFaqPage />} />
                <Route path="/ai-models" element={<AdminAiModelsPage />} />
              </Routes>
            </AuthenticatedLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/contractor/*"
        element={
          <ContractorRoute>
            <AuthenticatedLayout>
              <Routes>
                <Route path="/" element={<ContractorDashboard />} />
                <Route path="/surveys" element={<ContractorSurveys />} />
                <Route path="/surveys/new" element={<ContractorSurveyEdit />} />
                <Route path="/surveys/:id/edit" element={<ContractorSurveyEdit />} />
                <Route path="/surveys/:surveyId" element={<ContractorSurveyDetail />} />
                <Route path="/surveys/:surveyId/analysis" element={<SurveyAnalysisPage />} />
                <Route path="/surveys/:surveyId/questionnaire-report" element={<SurveyQuestionnaireReport />} />
                <Route path="/reports" element={<ContractorReports />} />
              </Routes>
            </AuthenticatedLayout>
          </ContractorRoute>
        }
      />
      <Route
        path="/participant/*"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Routes>
                <Route path="/surveys" element={<ParticipantDashboard />} />
                <Route path="/engagement" element={<ParticipantEngagementPage />} />
                <Route path="/surveys/:surveyId/take" element={<TakeSurvey />} />
              </Routes>
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ConfigProvider theme={pepollTheme} locale={ptBR}>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <CookieBanner />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ConfigProvider>
  );
}

export default App;
